import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, MessageCircle } from "lucide-react";
import { useLocale } from "@/i18n/format";
import { useInboxConversations } from "@/hooks/use-inbox-conversations";
import { useInboxComments } from "@/hooks/use-inbox-comments";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { formatRelativeTime } from "@/lib/upcoming-posts";
import { cn } from "@/lib/utils";

const LIMIT = 5;

interface LatestMessagesProps {
  brandId: string;
  brandSlug: string;
  /** null when the brand has no inbox; undefined while that is still loading. */
  unread: { dms: number; comments: number } | null | undefined;
  canManageBrand: boolean;
}

type Tab = "dms" | "comments";

/** The newest DMs and comments, so replies are one click away. */
export function HomeLatestMessages({ brandId, brandSlug, unread, canManageBrand }: LatestMessagesProps) {
  const { t } = useTranslation("dashboard");
  const [tab, setTab] = useState<Tab>("dms");
  const inboxEnabled = unread !== null && unread !== undefined;

  return (
    <section aria-labelledby="home-latest-messages" className="flex flex-col rounded-xl border bg-card">
      <header className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <h2 id="home-latest-messages" className="text-sm font-semibold">
          {t("home.messages.title")}
        </h2>
        {inboxEnabled && (
          <Link
            to={`/${brandSlug}/inbox/${tab}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {t("home.messages.openInbox")}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        )}
      </header>

      {unread === undefined ? (
        <ListSkeleton />
      ) : unread === null ? (
        <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <MessageCircle className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          </span>
          <p className="max-w-xs text-xs text-muted-foreground">{t("home.messages.notSetUp")}</p>
          {canManageBrand && (
            <Button asChild size="sm" variant="outline" className="mt-2">
              <Link to={`/${brandSlug}/settings/accounts`}>{t("home.messages.setUp")}</Link>
            </Button>
          )}
        </div>
      ) : (
        <>
          <div role="tablist" aria-label={t("home.messages.title")} className="flex gap-1 px-3 pt-3">
            {(["dms", "comments"] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition",
                  tab === key ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t(`home.messages.${key}`)}
                {unread[key] > 0 && (
                  <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground">
                    {unread[key] > 99 ? "99+" : unread[key]}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div role="tabpanel" className="flex-1">
            {tab === "dms" ? (
              <DmList brandId={brandId} brandSlug={brandSlug} />
            ) : (
              <CommentList brandId={brandId} brandSlug={brandSlug} />
            )}
          </div>
        </>
      )}
    </section>
  );
}

function DmList({ brandId, brandSlug }: { brandId: string; brandSlug: string }) {
  const { t } = useTranslation("dashboard");
  const locale = useLocale();
  const { data, isLoading, isError, refetch } = useInboxConversations({ brandId });
  const items = (data?.pages[0]?.conversations ?? []).slice(0, LIMIT);

  if (isLoading) return <ListSkeleton />;
  if (isError) return <ListError onRetry={() => void refetch()} />;
  if (items.length === 0) return <ListEmpty text={t("home.messages.emptyDms")} />;

  return (
    <ul className="p-2">
      {items.map((c) => (
        <MessageRow
          key={c.id}
          to={`/${brandSlug}/inbox/dms/${c.id}`}
          name={c.participantName ?? t("home.messages.someone")}
          avatar={c.participantAvatar}
          platform={c.platform}
          text={c.lastMessageText || t("home.messages.noText")}
          when={formatRelativeTime(new Date(c.lastMessageAt), locale)}
          unread={c.unreadCount > 0}
        />
      ))}
    </ul>
  );
}

function CommentList({ brandId, brandSlug }: { brandId: string; brandSlug: string }) {
  const { t } = useTranslation("dashboard");
  const locale = useLocale();
  const { data, isLoading, isError, refetch } = useInboxComments({ brandId });
  const items = (data?.pages[0]?.comments ?? []).slice(0, LIMIT);

  if (isLoading) return <ListSkeleton />;
  if (isError) return <ListError onRetry={() => void refetch()} />;
  if (items.length === 0) return <ListEmpty text={t("home.messages.emptyComments")} />;

  return (
    <ul className="p-2">
      {items.map((c) => (
        <MessageRow
          key={c.id}
          to={`/${brandSlug}/inbox/comments/${c.id}`}
          name={c.authorName ?? t("home.messages.someone")}
          avatar={c.authorAvatar}
          text={c.text || t("home.messages.noText")}
          when={formatRelativeTime(new Date(c.postedAt), locale)}
          unread={false}
          thumbnail={c.post.thumbnailUrl}
        />
      ))}
    </ul>
  );
}

interface MessageRowProps {
  to: string;
  name: string;
  avatar: string | null;
  platform?: string;
  text: string;
  when: string;
  unread: boolean;
  thumbnail?: string | null;
}

function MessageRow({ to, name, avatar, platform, text, when, unread, thumbnail }: MessageRowProps) {
  const { t } = useTranslation("dashboard");
  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="relative shrink-0">
          <Avatar className="h-9 w-9">
            {avatar && <AvatarImage src={avatar} alt="" />}
            <AvatarFallback className="text-xs">{initials || "?"}</AvatarFallback>
          </Avatar>
          {platform && (
            <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-card p-0.5">
              <PlatformIcon platform={platform} size={12} />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className={cn("truncate text-sm", unread ? "font-semibold" : "font-medium")}>{name}</span>
            <span className="shrink-0 text-[11px] text-muted-foreground">{when}</span>
          </span>
          <span className={cn("block truncate text-xs", unread ? "text-foreground" : "text-muted-foreground")}>
            {text}
          </span>
        </span>
        {thumbnail && (
          <img src={thumbnail} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded-md object-cover" />
        )}
        {unread && (
          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label={t("home.messages.unread")} />
        )}
      </Link>
    </li>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3 p-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ListEmpty({ text }: { text: string }) {
  return <p className="px-4 py-10 text-center text-xs text-muted-foreground">{text}</p>;
}

function ListError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="flex items-center justify-between gap-3 p-4">
      <p className="text-sm text-destructive">{t("home.messages.error")}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t("home.upNext.retry")}
      </Button>
    </div>
  );
}
